import { useEffect, useState } from "react";

/**
 * Theme persistence and switching.
 *
 * - localStorage key: "smc.theme" with value "light" | "dark"
 * - Default on first visit: "light" (per chunk 3r product decision — no
 *   system-preference detection)
 * - Applies via `<html data-theme="...">`. CSS variables in global.css
 *   flip automatically; component classes (bg-bg, text-text, etc.) need
 *   no changes.
 *
 * Pass 1 (chunk 3r): core tokens flip cleanly. Some inline color choices
 * elsewhere in the app (literal hex values, bg-purple-100, etc.) won't
 * flip until a dedicated audit pass — see roadmap notes in the SETUP doc.
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "smc.theme";

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* localStorage may be blocked — fall through */
  }
  return "light";
}

function applyTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

/**
 * Apply the stored theme as early as possible — on module load — so the
 * page doesn't flash white before React mounts. Calling at import time is
 * safe because this module is only imported in the browser bundle.
 */
applyTheme(readStoredTheme());

export function useTheme(): {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
} {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  return {
    theme,
    toggle: () => setThemeState((t) => (t === "light" ? "dark" : "light")),
    setTheme: setThemeState,
  };
}
